export type Asset = {
  id: string;
  sn: string;
  type: string;  // Changed to support dynamic types from database
  install_name: string;
  mac: string;
  customerId: string;
  active: boolean;
  last_communication?: string;
  value_type_id?: number;
  value_type_name?: string;
  datatype_id?: number;
  datatype_name?: string;
};

export type UpsertAsset = Omit<Asset, 'id'>;

export type AssetVersionHistory = {
  id: string;
  asset_id: string;
  version: string;
  datetime: string;
};

export type AssetNameHistory = {
  id: string;
  asset_id: string;
  name: string;
  datetime: string;
};

export type AssetDatatypeHistory = {
  id: string;
  asset_id: string;
  datatype: number;
  datetime: string;
};

export type AssetValueHistory = {
  id: string;
  asset_id: string;
  value_type: number;
  datetime: string;
};

export type AssetAllHistory = {
  version_history: AssetVersionHistory[];
  name_history: AssetNameHistory[];
  datatype_history: AssetDatatypeHistory[];
  value_history: AssetValueHistory[];
};

import { isTestMode } from './dataService';
import api from './api';

function buildDummyAssets(): Asset[] {
  const types: Asset['type'][] = ['TT_Logger', 'TT_Gateway'];
  return Array.from({ length: 20 }, (_, i) => ({
    id: `as-${i + 1}`,
    sn: `SN-${1000 + i}`,
    type: types[i % types.length],
    install_name: `Install ${i + 1}`,
    mac: `AA:BB:CC:DD:EE:${(i + 10).toString(16).toUpperCase()}`,
    customerId: `cust-${(i % 5) + 1}`,
    active: i % 5 !== 0, // Every 5th asset is inactive for testing
    last_communication: i % 3 === 0 ? undefined : new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString()
  }));
}

export async function listUserAssets(params?: { sn?: string; type?: Asset['type']; customerId?: string }) {
  if (isTestMode()) {
    let items = buildDummyAssets();
    if (params?.sn) items = items.filter(a => a.sn.includes(params.sn!));
    if (params?.type) items = items.filter(a => a.type === params.type);
    if (params?.customerId) items = items.filter(a => a.customerId === params.customerId);
    return Promise.resolve({ items, total: items.length });
  }
  const res = await api.get('/user-assets/user', { params });
  return res.data as { items: Asset[]; total: number };
}

export async function listAssets(params?: { sn?: string; type?: Asset['type']; customerId?: string }) {
  if (isTestMode()) {
    let items = buildDummyAssets();
    if (params?.sn) items = items.filter(a => a.sn.includes(params.sn!));
    if (params?.type) items = items.filter(a => a.type === params.type);
    if (params?.customerId) items = items.filter(a => a.customerId === params.customerId);
    return Promise.resolve({ items, total: items.length });
  }
  const res = await api.get('/assets', { params });
  return res.data as { items: Asset[]; total: number };
}

export async function getAsset(id: string) {
  if (isTestMode()) {
    return Promise.resolve(buildDummyAssets().find(a => a.id === id) || buildDummyAssets()[0]);
  }
  // Try user endpoint first, fall back to admin endpoint
  try {
    const res = await api.get(`/user-assets/${id}`);
    return res.data as Asset;
  } catch (error: unknown) {
    if (error instanceof Error && 'response' in error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 403 || axiosError.response?.status === 404) {
        // Try admin endpoint as fallback
        const res = await api.get(`/assets/${id}`);
        return res.data as Asset;
      }
    }
    throw error;
  }
}

export async function createAsset(payload: UpsertAsset) {
  if (isTestMode()) {
    const id = `as-${Math.floor(Math.random() * 100000)}`;
    return Promise.resolve({ id, ...payload });
  }
  const res = await api.post('/assets', payload);
  return res.data as Asset;
}

export async function updateUserAsset(id: string, payload: { value_type_id?: number }) {
  if (isTestMode()) {
    const baseItem = buildDummyAssets()[0];
    return Promise.resolve({ ...baseItem, id, ...payload } as Asset);
  }
  const res = await api.patch(`/user-assets/${id}`, payload);
  return res.data as Asset;
}

export async function updateAsset(id: string, payload: Partial<UpsertAsset>) {
  if (isTestMode()) {
    const baseItem = buildDummyAssets()[0];
    return Promise.resolve({ ...baseItem, id, ...payload } as Asset);
  }
  const res = await api.patch(`/assets/${id}`, payload);
  return res.data as Asset;
}

export async function deleteAsset(id: string) {
  if (isTestMode()) {
    return Promise.resolve();
  }
  await api.delete(`/assets/${id}`);
}

export async function listAssetTypes() {
  if (isTestMode()) {
    // Return dummy data for testing
    return Promise.resolve([
      { id: '2', name: 'Gateway/Client', description: 'Gateway device' },
      { id: '3', name: 'Sensor (8x channels)', description: 'Multi-channel sensor' },
      { id: '4', name: 'Sensor (UI)', description: 'UI sensor' },
      { id: '5', name: 'Sensor (Temp)', description: 'Temperature sensor' },
      { id: '6', name: 'Default sensor', description: 'Default sensor type' }
    ]);
  }
  const res = await api.get('/user-assets/types');
  return res.data as { id: string; name: string; description: string }[];
}

export async function listAssetDatatypes() {
  if (isTestMode()) {
    // Return dummy data for testing
    return Promise.resolve([
      { id: '0', name: 'TT_Single_Data', suffix: '' },
      { id: '1', name: 'TT_Single_SyncReq', suffix: '' },
      { id: '12', name: 'TT_8x_CH1_Data', suffix: 'CH1' },
      { id: '13', name: 'TT_8x_CH2_Data', suffix: 'CH2' },
      { id: '22', name: 'TT_UI_CH1_U-Data', suffix: 'U-CH1' },
      { id: '23', name: 'TT_UI_CH1_I-Data', suffix: 'I-CH1' },
      { id: '32', name: 'TT_Temp_CH1', suffix: 'T-CH1' },
      { id: '33', name: 'TT_Temp_CH2', suffix: 'T-CH2' }
    ]);
  }
  const res = await api.get('/user-assets/datatypes');
  return res.data as { id: string; name: string; suffix: string }[];
}

export async function listAssetValueTypes() {
  if (isTestMode()) {
    // Return dummy data for testing
    return Promise.resolve([
      { id: '1', name: 'Wood Moisture [Std]', unit: 'Moisture', value: 'M-%' },
      { id: '2', name: 'Wood Moisture [Std tmp comp]', unit: 'Moisture', value: 'M-%' },
      { id: '3', name: 'Wood Moisture [Std new]', unit: 'Moisture', value: 'M-%' },
      { id: '4', name: 'Wood Moisture [Pine]', unit: 'Moisture', value: 'M-%' },
      { id: '5', name: 'Wood Moisture [OSB]', unit: 'Moisture', value: 'M-%' },
      { id: '6', name: 'Wood Moisture [Balsa]', unit: 'Moisture', value: 'M-%' },
      { id: '7', name: 'Voltage', unit: 'Voltage', value: 'V' },
      { id: '8', name: 'Current', unit: 'Current', value: 'A' },
      { id: '9', name: 'Temperature', unit: 'Temperature', value: '°C' },
      { id: '10', name: 'Default', unit: 'Default', value: 'N/A' }
    ]);
  }
  const res = await api.get('/user-assets/value-types');
  return res.data as { id: string; name: string; unit: string; value: string }[];
}

export async function getAssetHistory(assetId: string) {
  if (isTestMode()) {
    // Return dummy history data for testing
    return Promise.resolve({
      version_history: [
        { id: '1', asset_id: assetId, version: 'v2.1.0', datetime: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString() },
        { id: '2', asset_id: assetId, version: 'v2.0.0', datetime: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString() },
        { id: '3', asset_id: assetId, version: 'v1.5.0', datetime: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString() },
        { id: '4', asset_id: assetId, version: 'v1.0.0', datetime: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString() }
      ],
      name_history: [
        { id: '1', asset_id: assetId, name: 'Main Sensor - Updated', datetime: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString() },
        { id: '2', asset_id: assetId, name: 'Main Sensor', datetime: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString() },
        { id: '3', asset_id: assetId, name: 'Sensor Unit 1', datetime: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString() }
      ],
      datatype_history: [
        { id: '1', asset_id: assetId, datatype: 12, datetime: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString() },
        { id: '2', asset_id: assetId, datatype: 0, datetime: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString() }
      ],
      value_history: [
        { id: '1', asset_id: assetId, value_type: 1, datetime: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString() },
        { id: '2', asset_id: assetId, value_type: 10, datetime: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000).toISOString() }
      ]
    });
  }
  // Try user endpoint first, fall back to admin endpoint
  try {
    const res = await api.get(`/user-assets/${assetId}/history`);
    return res.data as AssetAllHistory;
  } catch (error: unknown) {
    if (error instanceof Error && 'response' in error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 403 || axiosError.response?.status === 404) {
        // Try admin endpoint as fallback
        const res = await api.get(`/assets/${assetId}/history`);
        return res.data as AssetAllHistory;
      }
    }
    throw error;
  }
}
